// src/features/assistant-connect/utils/catalog.ts
//
// What the assistant can be asked for, by whom. A connected assistant acts with the person's own
// Insight session, so a task is listed only to the profiles that can do it here: listing alerts to a
// hotel viewer would promise something the server then refuses. Only what the assistant's tools do
// today is declared; nothing in preparation is announced.
//
// Rows are declared here rather than inside the page so the labels of both languages can be
// asserted against them (see catalog.test.ts).

/** Insight profiles the assistant accepts (see access.ts). */
export type AssistantRole = 'SuperAdmin' | 'Admin' | 'GroupAdmin' | 'HotelAdmin' | 'Viewer';

export interface AssistantTask {
  /** Key under `assistantConnect.tasks.items` in the message catalogues. */
  key: string;
  roles: readonly AssistantRole[];
}

const EVERYONE: readonly AssistantRole[] = ['SuperAdmin', 'Admin', 'GroupAdmin', 'HotelAdmin', 'Viewer'];
// Alerts and the error monitor are not offered to hotel profiles or viewers in the screens.
const ALERT_VIEWERS: readonly AssistantRole[] = ['SuperAdmin', 'Admin', 'GroupAdmin'];

export const ASSISTANT_TASKS: readonly AssistantTask[] = [
  { key: 'properties', roles: EVERYONE },
  { key: 'indicators', roles: EVERYONE },
  { key: 'comparisons', roles: EVERYONE },
  { key: 'arrivals', roles: EVERYONE },
  { key: 'checkIns', roles: EVERYONE },
  { key: 'alerts', roles: ALERT_VIEWERS },
  { key: 'devices', roles: EVERYONE },
  { key: 'manual', roles: EVERYONE },
];

/** The tasks this profile can actually do, in catalogue order. */
export const tasksFor = (role: string | null | undefined): AssistantTask[] =>
  ASSISTANT_TASKS.filter((task) => (task.roles as readonly string[]).includes(role ?? ''));
